import { replaceNotebookVideosForExport } from "./notebookMedia"
import { printInFrame } from "./printInFrame"

/** Print the rendered document in isolation, preserving charts, artwork and authored colors. */
export async function printNotebookCanvas(id: string, title: string) {
  const canvas = Array.from(
    document.querySelectorAll<HTMLElement>("[data-notebook-canvas]"),
  ).find((element) => element.dataset.notebookCanvas === id)
  if (!canvas) {
    throw new Error("The notebook is still loading. Try again.")
  }
  await printInFrame(document.title, (target) => {
    for (const style of document.querySelectorAll(
      'style, link[rel="stylesheet"]',
    )) {
      target.head.append(style.cloneNode(true))
    }
    const copy = canvas.cloneNode(true) as HTMLElement
    copy
      .querySelectorAll("[data-drag-handle], input[type=range]")
      .forEach((element) => element.remove())
    copy
      .querySelectorAll("[contenteditable]")
      .forEach((element) => element.setAttribute("contenteditable", "false"))
    copy.querySelectorAll("details").forEach((element) => {
      element.open = true
    })
    // On the page a canvas heading renders one level below its saved level,
    // under the page's own h1. The printed document stands alone, so each
    // heading returns to its saved level and the title is the print's h1.
    copy
      .querySelectorAll<HTMLElement>(
        "h1[data-level], h2[data-level], h3[data-level], h4[data-level], h5[data-level], h6[data-level]",
      )
      .forEach((element) => {
        const level = Number(element.dataset.level)
        if (!Number.isInteger(level) || level < 1 || level > 6) {
          return
        }
        const heading = target.createElement(`h${level}`)
        for (const attribute of element.attributes) {
          heading.setAttribute(attribute.name, attribute.value)
        }
        heading.append(...element.childNodes)
        element.replaceWith(heading)
      })
    // A tagged PDF needs a document heading; a canvas without one gets its title.
    if (!copy.querySelector("h1")) {
      const heading = target.createElement("h1")
      heading.textContent = title
      copy.prepend(heading)
    }
    replaceNotebookVideosForExport(copy)
    labelChartGraphics(copy)
    target.body.append(copy)
    return undefined
  })
}

/**
 * Chromium's tagged print records a Figure with alt text for an image or for
 * a `figure` with role img and a name; an inline SVG alone is left untagged.
 * Each rendered chart outside a named graphic is wrapped in one, named after
 * its card title.
 */
function labelChartGraphics(root: HTMLElement) {
  root
    .querySelectorAll<SVGSVGElement>("svg.recharts-surface")
    .forEach((svg) => {
      svg.removeAttribute("tabindex")
      svg.removeAttribute("role")
      if (svg.parentElement?.closest('[role="img"][aria-label]')) {
        return
      }
      const title = svg
        .closest('[data-testid="covi-viz-card"]')
        ?.firstElementChild?.textContent?.trim()
      const figure = svg.ownerDocument.createElement("figure")
      figure.setAttribute("role", "img")
      figure.setAttribute(
        "aria-label",
        svg.getAttribute("aria-label") || (title ? `Chart: ${title}` : "Chart"),
      )
      figure.style.margin = "0"
      svg.replaceWith(figure)
      figure.append(svg)
    })
}
